'use client';
import React, {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { StateManagerConfig } from '../types';
import { createApiClient } from '../api/client';
import { createApiService } from '../api/services';
import { getQueryClient } from './query-client';
import { useAuthStore } from '../stores/auth-store';

interface StateManagerContextValue {
  config: StateManagerConfig;
  queryClient: QueryClient;
}

const StateManagerContext = createContext<StateManagerContextValue | null>(
  null
);

export interface StateManagerProviderProps {
  children: ReactNode;
  config: StateManagerConfig;
  onAuthRequired?: () => void;
  onAuthRefresh?: () => Promise<boolean>;
}

export function StateManagerProvider({
  children,
  config,
  onAuthRequired,
  onAuthRefresh,
}: StateManagerProviderProps) {
  // Initialize API client and services
  React.useMemo(() => {
    const apiClient = createApiClient(config);

    // Set up auth handlers
    apiClient.setAuthHandlers({
      onUnauthorized: onAuthRequired,
    });

    // Create API service (singleton)
    createApiService();
  }, [config, onAuthRequired, onAuthRefresh]);

  // Get or create query client
  const queryClient = React.useMemo(() => getQueryClient(config), [config]);

  const contextValue = React.useMemo(
    () => ({
      config,
      queryClient,
    }),
    [config, queryClient]
  );

  return (
    <StateManagerContext.Provider value={contextValue}>
      <QueryClientProvider client={queryClient}>
        <AuthInitializer>{children}</AuthInitializer>
      </QueryClientProvider>
    </StateManagerContext.Provider>
  );
}

// Component to initialize auth state on app start
function AuthInitializer({ children }: { children: ReactNode }) {
  const { actions } = useAuthStore();

  useEffect(() => {
    // Don't refresh if we're on login page
    if (window.location.pathname === '/login') {
      return;
    }

    // If we have a cached user session, validate it with server
    // If no cached session, try to get user (might have valid HTTP-only cookie)
    const initializeAuth = async () => {
      try {
        await actions.refreshUser();
      } catch (error) {
        // Only log in dev, silently fail in production
        if (process.env.NODE_ENV === 'development') {
          console.log('No valid session found on app start');
        }
      }
    };

    initializeAuth();

    // Also check for URL search params that might indicate a redirect from landing page
    const urlParams = new URLSearchParams(window.location.search);
    const fromLanding = urlParams.get('from') === 'landing';

    if (fromLanding) {
      // User was redirected from landing page, give a bit more time for cookies to be available
      // and retry authentication after a short delay
      const retryTimer = setTimeout(() => {
        initializeAuth();
      }, 500);

      return () => clearTimeout(retryTimer);
    }
  }, [actions]);

  return <>{children}</>;
}

export function useStateManager(): StateManagerContextValue {
  const context = useContext(StateManagerContext);
  if (!context) {
    throw new Error(
      'useStateManager must be used within a StateManagerProvider'
    );
  }
  return context;
}
